import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface FloatingChatContextValue {
  /** Whether the chat popup is open */
  isOpen: boolean;
  /** Open the chat popup */
  openChat: () => void;
  /** Close the chat popup */
  closeChat: () => void;
  /** Toggle the chat popup */
  toggleChat: () => void;
  /** Current pathname (reactive to route changes) */
  pathname: string;
}

const FloatingChatContext = createContext<FloatingChatContextValue | null>(null);

interface FloatingChatProviderProps {
  children: ReactNode;
}

/**
 * Provider for floating chat state
 * Manages chat open/close state globally and tracks pathname for route-based visibility
 */
export function FloatingChatProvider({ children }: FloatingChatProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pathname, setPathname] = useState(window.location.pathname);

  // Listen for route changes (works with React Router's navigation)
  useEffect(() => {
    const handleLocationChange = () => {
      setPathname(window.location.pathname);
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);

    // Use MutationObserver to detect React Router's navigation
    // (React Router changes the URL without triggering popstate)
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== pathname) {
        setPathname(window.location.pathname);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [pathname]);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    <FloatingChatContext.Provider
      value={{
        isOpen,
        openChat,
        closeChat,
        toggleChat,
        pathname,
      }}
    >
      {children}
    </FloatingChatContext.Provider>
  );
}

/**
 * Hook to access floating chat state
 */
export function useFloatingChat() {
  const context = useContext(FloatingChatContext);
  if (!context) {
    throw new Error('useFloatingChat must be used within a FloatingChatProvider');
  }
  return context;
}
